import React, { useState } from 'react';
import './App.css';

function App() {
  const [activeSection, setActiveSection] = useState('home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    setIsMobileMenuOpen(false); // Close mobile menu when navigating
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Header */}
      <nav className="fixed top-0 w-full bg-white shadow-lg z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex-shrink-0">
              <img
                src="/astravrik-logo.png"
                alt="AstraVrik Technologies"
                className="h-12 w-auto"
              />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                {['home', 'about', 'services', 'products', /* 'founder', */ 'contact'].map((section) => (
                  <button
                    key={section}
                    onClick={() => scrollToSection(section)}
                    className={`px-3 py-2 rounded-md text-sm font-medium capitalize transition-colors ${activeSection === section
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:bg-blue-100 hover:text-blue-600'
                      }`}
                  >
                    {section}
                  </button>
                ))}
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-md text-gray-600 hover:text-blue-600 hover:bg-gray-100"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t">
                {['home', 'about', 'services', 'products', /* 'founder', */ 'contact'].map((section) => (
                  <button
                    key={section}
                    onClick={() => scrollToSection(section)}
                    className={`block px-3 py-2 rounded-md text-base font-medium capitalize w-full text-left transition-colors ${activeSection === section
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:bg-blue-100 hover:text-blue-600'
                      }`}
                  >
                    {section}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Home Section */}
      <section id="home" className="pt-16 min-h-screen flex items-center bg-gradient-to-r from-blue-600 to-purple-700">
        {/* Star background decoration */}
        <div className="absolute top-10 right-5 w-48 h-48 opacity-10 pointer-events-none" aria-hidden="true">
          <div className="w-full h-full bg-white rounded-full"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            AI‑Driven. Future‑Ready. <span className="text-yellow-300">Solution Assured.</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-4xl mx-auto">
            Empowering enterprises with intelligent, adaptive, and secure AI solutions for BFSI, Legal, Healthcare, and beyond. Founded in Mumbai, serving globally.
          </p>
          <button
            onClick={() => scrollToSection('services')}
            className="bg-white text-blue-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors"
            aria-label="Explore our AI solutions and services"
          >
            Explore Solutions
          </button>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white" aria-labelledby="about-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="about-heading" className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">About AstraVrik Technologies - Leading AI Company in Mumbai</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Premier AI-driven digital transformation company specializing in BFSI, Legal, and Healthcare technology solutions since 2020.
            </p>
          </div>

          <div className="space-y-6 text-lg leading-relaxed mb-12">
            <p className="text-gray-600">
              Founded in 2020 in Mumbai, <strong>AstraVrik Technologies LLP</strong> was born with a singular vision: to empower businesses to think beyond constraints and embrace intelligent transformation. While the world faced uncertainty, we focused on creating opportunities—helping enterprises achieve their goals through <em>AI‑driven solutions</em>, digital innovation, and technology‑enabled growth.
            </p>

            <p className="text-gray-600">
              We are a forward‑thinking <strong>technology company</strong> specializing in <em>AI‑powered digital solutions</em>, advanced data analytics, business advisory & technology consultancy, and software consultancy services. Our team brings decades of collective expertise in delivering complex IT transformation projects across <strong>BFSI</strong>, <strong>legal</strong>, <strong>healthcare</strong>, and other industries. We are obsessed with client success, trust, and integrity. At AstraVrik, we live by our philosophy of Going the Extra Mile (GEM). We continuously seek smarter, faster, and more innovative ways to solve business challenges—delivering solutions that are intelligent, scalable, and assured.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="p-6 rounded-lg bg-blue-50 border border-blue-100">
              <h3 className="text-2xl font-bold mb-4 text-blue-600">Vision</h3>
              <p className="text-gray-600">
                To reimagine enterprise intelligence by transforming conventional processes into intelligent ecosystems that fuel innovation and growth.
              </p>
            </div>

            <div className="p-6 rounded-lg bg-blue-50 border border-blue-100">
              <h3 className="text-2xl font-bold mb-4 text-blue-600">Mission</h3>
              <p className="text-gray-600">
                To be a ray of assurance for our clients—transforming goals into success stories through customized AI solutions, delivered with unwavering integrity and business ethics.
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-bold mb-8 text-center text-gray-900">Values</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-6 rounded-lg bg-gray-50 border border-gray-200">
                <h4 className="text-lg font-semibold mb-3 text-blue-600">Customers</h4>
                <p className="text-gray-600 text-sm">We serve with passion and excellence. We innovate to create lasting impact. We empower transformation through digital intelligence.</p>
              </div>
              <div className="p-6 rounded-lg bg-gray-50 border border-gray-200">
                <h4 className="text-lg font-semibold mb-3 text-blue-600">People</h4>
                <p className="text-gray-600 text-sm">We value diversity, fairness, and respect. We communicate openly and honestly. We foster safe, empowering workplaces.</p>
              </div>
              <div className="p-6 rounded-lg bg-gray-50 border border-gray-200">
                <h4 className="text-lg font-semibold mb-3 text-blue-600">Quality</h4>
                <p className="text-gray-600 text-sm">We aim for excellence in every project. We continuously evolve to stay ahead.</p>
              </div>
              <div className="p-6 rounded-lg bg-gray-50 border border-gray-200">
                <h4 className="text-lg font-semibold mb-3 text-blue-600">Integrity</h4>
                <p className="text-gray-600 text-sm">Integrity is our currency. We uphold the highest ethical standards. We do what is right, always.</p>
              </div>
            </div>
          </div>
        </div>
      </section>      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-100" aria-labelledby="services-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="services-heading" className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">AI Solutions & Digital Transformation Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive AI-powered solutions tailored for BFSI, Legal, Healthcare, and enterprise business needs across Mumbai and globally.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 mb-12">
            <article className="p-6 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold mb-4 text-blue-600">Business Consultancy & Advisory Services</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Business Advisory & Software Consultancy</li>
                <li>• Digital Strategy & Innovation Planning</li>
                <li>• Process Audits & Optimization</li>
                <li>• Technology Roadmap Development</li>
              </ul>
            </article>

            <article className="p-6 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold mb-4 text-blue-600">AI-Powered Digital Transformation</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• AI‑powered Enterprise Transformation</li>
                <li>• End‑to‑End Product Engineering</li>
                <li>• Automation & AI Integration</li>
                <li>• Machine Learning Implementation</li>
              </ul>
            </article>

            <article className="p-6 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold mb-4 text-blue-600">Custom SaaS Solutions Development</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• UI/UX Design & Development</li>
                <li>• Web & Mobile Applications</li>
                <li>• Resource Augmentation Services</li>
                <li>• AI‑enabled SaaS Platforms</li>
              </ul>
            </article>

            <article className="p-6 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold mb-4 text-blue-600">AI & Data Analytics Solutions</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• AI Knowledge Centre (Flagship Product)</li>
                <li>• Predictive Analytics & Business Insights</li>
                <li>• Conversational AI (Voice/Text Chatbots)</li>
                <li>• Compliance & Risk Management Tools</li>
              </ul>
            </article>
          </div>

          <div className="p-6 rounded-lg bg-blue-600 text-white">
            <h3 className="text-xl font-bold mb-4">Financial Services Digital Transformation Mission</h3>
            <p>
              We are on a mission to digitally transform the <strong>financial services sector</strong> by providing affordable, future‑ready technology platforms to every player—enabling even the smallest service providers to embrace AI‑driven, web‑ready growth in the BFSI industry.
            </p>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 bg-white" aria-labelledby="products-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="products-heading" className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">AI Products & Industry Solutions - BFSI, Legal, Healthcare</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Innovative AI-powered products designed to solve real-world problems in Banking, Financial Services, Legal Technology, and Healthcare sectors.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <article className="p-6 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white">
              <h3 className="text-xl font-bold mb-4">AI Knowledge Centre - Flagship Product</h3>
              <p className="mb-4">
                An intelligent, <strong>AI‑driven repository</strong> with context‑aware search, chatbot interfaces, retrieval‑augmented insights, and automated compliance checks. Perfect for BFSI and legal firms requiring quick access to regulatory information.
              </p>
              {/* <button
                className="bg-white text-blue-600 px-6 py-2 rounded-full font-semibold hover:bg-gray-100"
                aria-label="Learn more about AI Knowledge Centre"
              >
                Learn More
              </button> */}
            </article>

            <article className="p-6 rounded-lg bg-gradient-to-r from-green-500 to-teal-600 text-white">
              <h3 className="text-xl font-bold mb-4">Business Advisory & Technology Consultancy</h3>
              <p className="mb-4">
                Strategic advisory and end‑to‑end <strong>technology consultancy services</strong>, enabling clients to define roadmaps, reduce risks, and accelerate digital transformation with AI at the core. Specialized in Mumbai-based enterprises and global clients.
              </p>
              {/* <button
                className="bg-white text-green-600 px-6 py-2 rounded-full font-semibold hover:bg-gray-100"
                aria-label="Learn more about our consultancy services"
              >
                Learn More
              </button> */}
            </article>

            <article className="p-6 rounded-lg bg-gradient-to-r from-purple-500 to-pink-600 text-white">
              <h3 className="text-xl font-bold mb-4">Custom AI Software Development</h3>
              <p className="mb-4">
                Bespoke <strong>AI/ML‑powered applications</strong> tailored to unique industry needs—delivering scalable, secure, and high‑performing solutions for enterprise digital transformation across multiple sectors.
              </p>
              {/* <button
                className="bg-white text-purple-600 px-6 py-2 rounded-full font-semibold hover:bg-gray-100"
                aria-label="Learn more about custom software development"
              >
                Learn More
              </button> */}
            </article>

            <article className="p-6 rounded-lg bg-gradient-to-r from-orange-500 to-red-600 text-white">
              <h3 className="text-xl font-bold mb-4">Industry-Specific AI Solutions</h3>
              <div className="space-y-2 mb-4">
                <p><strong>BFSI:</strong> Digital lending platforms, compliance automation, fraud detection systems</p>
                <p><strong>Legal:</strong> Precedent tracking, smart knowledge repositories, case management</p>
                <p><strong>Healthcare:</strong> AI‑enabled knowledge hubs & regulatory compliance frameworks</p>
              </div>
              {/* <button
                className="bg-white text-orange-600 px-6 py-2 rounded-full font-semibold hover:bg-gray-100"
                aria-label="Learn more about industry solutions"
              >
                Learn More
              </button> */}
            </article>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      {/* <section id="founder" className="py-20 bg-gray-100" aria-labelledby="founder-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="founder-heading" className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Meet Our Founder - Manoj Shah, CTO of the Year 2025</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Visionary leader with 28+ years of global IT transformation experience across BFSI, Legal, and Healthcare sectors.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <article className="p-8 rounded-lg bg-white shadow-lg">
              <div className="md:flex items-start gap-8">
                <div className="md:w-1/4 text-center mb-6 md:mb-0">
                  <div className="w-32 h-32 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full mx-auto mb-4 flex items-center justify-center" aria-hidden="true">
                    <span className="text-4xl text-white">👨‍💼</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Manoj Shah</h3>
                  <p className="font-semibold text-blue-600">Founder & Visionary</p>
                  <p className="text-sm text-gray-600 italic mt-2"><strong>CTO of the Year (NBFC) - 2025</strong></p>
                </div>
                <div className="md:w-3/4">
                  <div className="space-y-4 text-gray-600">
                    <p>
                      Technology and finance excite <strong>Manoj Shah</strong>, the visionary founder of <em>AstraVrik Technologies LLP</em>. With over <strong>28 years of global experience</strong>, he has spearheaded <strong>23+ large‑scale IT transformation projects</strong> across India, Asia‑Pacific, the US, Canada, and Europe.
                    </p>
                    <p>
                      Widely recognized as <em><strong>CTO of the Year (NBFC)</strong></em> in 2025, Manoj has consistently delivered complex, high‑impact solutions that have earned him accolades from leading banks in Canada, Singapore, Malaysia, and Austria. His passion for innovation, combined with deep domain expertise in <strong>BFSI technology</strong>, laid the foundation for AstraVrik.
                    </p>
                    <p>
                      When not immersed in technology, Manoj enjoys reading, solving intricate puzzles, and building advanced models in Excel—testament to his analytical mind and relentless curiosity. His leadership drives AstraVrik's mission to transform enterprises through <strong>AI-driven solutions</strong>.
                    </p>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section> */}

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-900 text-white" aria-labelledby="contact-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="contact-heading" className="text-3xl md:text-4xl font-bold mb-4">Contact AstraVrik Technologies - Mumbai AI Company</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Ready to start your AI transformation journey? Contact our Mumbai-based team for expert consultation on digital transformation projects.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="bg-gray-800 rounded-lg p-8">
              <h3 className="text-2xl font-bold mb-6 text-center">Get In Touch with Our AI Experts</h3>
              <address className="space-y-6 not-italic">
                <div className="flex items-center justify-center">
                  <span className="text-3xl mr-4" role="img" aria-label="Location">📍</span>
                  <div className="text-center">
                    <p className="font-semibold text-lg">Head Office Location</p>
                    <p className="text-gray-300">Vasai West, Mumbai, Maharashtra 401201, India</p>
                    <p className="text-sm text-gray-400 mt-1">Serving Mumbai Metropolitan Area & Global Clients</p>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <span className="text-3xl mr-4" role="img" aria-label="Email">📧</span>
                  <div className="text-center">
                    <p className="font-semibold text-lg">Business Inquiries</p>
                    <a href="mailto:wecare@astravrik.com" className="text-blue-400 hover:underline text-lg">
                      wecare@astravrik.com
                    </a>
                    <p className="text-sm text-gray-400 mt-1">AI Solutions • Digital Transformation • Technology Consultancy</p>
                  </div>
                </div>
              </address>

              <div className="mt-8 text-center">
                <h4 className="text-lg font-semibold mb-3">Industry Expertise</h4>
                <p className="text-gray-300 text-sm">
                  <strong>BFSI</strong> • <strong>Legal Technology</strong> • <strong>Healthcare AI</strong> •
                  <strong>Custom Software Development</strong> • <strong>Business Consultancy</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">
            © 2025 AstraVrik Technologies LLP. All rights reserved. Built with React & Tailwind CSS.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
